/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Warning as WarningIcon, Update as UpdateIcon } from '@mui/icons-material';
import { getLastAppVersion } from '../api/employeePortal';

interface VersionResponse {
    success: boolean;
    version: number;
    requiresLogout: boolean;
    changelog?: string;
    isCritical?: boolean;
    shouldAskUser?: boolean;
}

interface UpdateManagerProps {
    checkInterval?: number; 
}

const LS_APP_VERSION_KEY = 'app_version';
const LS_USER_SESSION_KEY = 'sessionData';


const UpdateManager: React.FC<UpdateManagerProps> = ({
    checkInterval = 3600000 // 1 hora
}) => {
    const hasInitialised = useRef(false);
    const [open, setOpen] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [updateInfo, setUpdateInfo] = useState<{
        message: string;
        changelog?: string;
        requiresLogout: boolean;
        isCritical: boolean;
    } | null>(null);

    const hardRefreshCache = async (): Promise<void> => {
        try {
            
        if ('caches' in window) {
            const cacheKeys = await caches.keys();
            await Promise.all(cacheKeys.map(key => caches.delete(key)));
        }

        sessionStorage.clear();

        const reloadKey = `reload_${Date.now()}`;
        localStorage.setItem('cache_reload_key', reloadKey);

        const links = document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]');
        links.forEach(link => {
            const url = new URL(link.href);
            url.searchParams.set('forceReload', reloadKey);
            link.href = url.toString();
        });

        } catch (error) {
        console.error('Error clearing cache:', error);
        }
    };

    const clearSessionData = (): void => {
        const preservedData = {
            app_version: localStorage.getItem(LS_APP_VERSION_KEY),
        };

        localStorage.clear();
        sessionStorage.clear();

        if (preservedData.app_version) {
            localStorage.setItem(LS_APP_VERSION_KEY, preservedData.app_version);
        }
    };

    const checkVersion = async (): Promise<void> => {
        setIsChecking(true);
        try {
            const data: VersionResponse = (await getLastAppVersion())?.data
            const storedVersion = JSON.parse(localStorage.getItem(LS_APP_VERSION_KEY) || '{}') as VersionResponse;
            const currentSessionActive = localStorage.getItem(LS_USER_SESSION_KEY);
            

            if (data?.success && data?.version && (storedVersion.version !== data.version)) {
                console.log('version is different')
                localStorage.setItem(LS_APP_VERSION_KEY, JSON.stringify(data));
                const updateData = {
                    message: `Estimado usuario de S10, hemos realizado algunas mejoras en la plataforma. Para que estos cambios se apliquen correctamente, es necesario actualizar la página. Por favor, confirma para continuar con el proceso de actualización.`,
                    ...data,
                    changelog: data.changelog,
                    isCritical: data.isCritical || false
                };
                setUpdateInfo(updateData);

                await hardRefreshCache();

                if (data.requiresLogout && currentSessionActive) {
                    if (data.shouldAskUser) {
                        setOpen(true);
                    } else {
                        clearSessionData();
                        window.location.reload();
                    }
                } else if (data.isCritical) {
                    window.location.reload();
                } else if (data.shouldAskUser && currentSessionActive) {
                    setOpen(true);
                } else {
                    window.location.reload();
                }
            }
        } catch (error) {
            console.error('Error al verificar versión:', error);
        } finally {
            setIsChecking(false);
        }
    };

    useEffect(() => {
        if (hasInitialised.current) return;
        hasInitialised.current = true;
    
        void checkVersion();
        const id = setInterval(checkVersion, checkInterval);
    
        return () => clearInterval(id);
    }, []);

    const handleConfirm = (): void => {
        if (updateInfo?.requiresLogout) {
            clearSessionData();
        }
        window.location.reload();
    };

    const handleClose = (): void => {
        setOpen(false);
        if (updateInfo?.isCritical) {
        setTimeout(() => window.location.reload(), 5000);
        }
    };

    return (
        <>
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="update-dialog-title"
            maxWidth="md"
            fullWidth
        >
            <DialogTitle id="update-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <UpdateIcon color="primary" />
                <Typography variant="h6" component="span">
                    Actualización Disponible
                </Typography>
            </DialogTitle>
        
            <DialogContent dividers>
                {updateInfo?.isCritical && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                    Esta es una actualización crítica de seguridad
                    </Alert>
                )}
                
                <Typography variant="body1" paragraph>
                    {updateInfo?.message}
                </Typography>
            
                {updateInfo?.changelog && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Novedades en esta versión:
                    </Typography>
                    <Typography variant="body2" whiteSpace="pre-line">
                        {updateInfo.changelog}
                    </Typography>
                    </Box>
                )}
            
                {updateInfo?.requiresLogout && (
                    <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
                        Después de la actualización, tu sesión se cerrará y será necesario que vuelvas a iniciar sesión.
                    </Alert>
                )}
            </DialogContent>
            
                <DialogActions sx={{ p: 2 }}>
                {(!updateInfo?.isCritical && !updateInfo?.requiresLogout) && (
                    <Button onClick={handleClose} color="secondary">
                        Posponer
                    </Button>
                )}
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color={updateInfo?.isCritical ? 'error' : 'primary'}
                    startIcon={isChecking ? <CircularProgress size={20} /> : null}
                    disabled={isChecking}
                >
                    {updateInfo?.isCritical ? 'Actualizar Ahora' : 'Aplicar Actualización'}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default UpdateManager;